import iconPlus from '@phosphor-icons/core/regular/plus.svg';
import React from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import ScrollableList from '@/components/scrollable-list';
import Column from '@/components/ui/column';
import Icon from '@/components/ui/icon';
import { useRules } from '@/queries/admin/use-rules';
import { useModalsActions } from '@/stores/modals';
import toast from '@/toast';

import type { AdminRule } from 'pl-api';

const messages = defineMessages({
  heading: { id: 'column.admin.rules', defaultMessage: 'Instance rules' },
  deleteConfirm: { id: 'confirmations.admin.delete_rule.confirm', defaultMessage: 'Delete' },
  deleteHeading: { id: 'confirmations.admin.delete_rule.heading', defaultMessage: 'Delete rule' },
  deleteMessage: {
    id: 'confirmations.admin.delete_rule.message',
    defaultMessage: 'Are you sure you want to delete the rule?',
  },
  ruleDeleteSuccess: { id: 'admin.edit_rule.deleted', defaultMessage: 'Rule deleted' },
});

interface IRule {
  rule: AdminRule;
}

const Rule: React.FC<IRule> = ({ rule }) => {
  const intl = useIntl();
  const { openModal } = useModalsActions();
  const { deleteRule } = useRules();

  const handleEditRule = (rule: AdminRule) => () => {
    openModal('EDIT_RULE', { rule });
  };

  const handleDeleteRule = (id: string) => () => {
    openModal('CONFIRM', {
      heading: intl.formatMessage(messages.deleteHeading),
      message: intl.formatMessage(messages.deleteMessage),
      confirm: intl.formatMessage(messages.deleteConfirm),
      onConfirm: () => {
        deleteRule(id, {
          onSuccess: () => {
            toast.success(messages.ruleDeleteSuccess);
          },
        });
      },
    });
  };

  return (
    <div key={rule.id} className='rules__rule'>
      <p className='rules__rule__text'>{rule.text}</p>
      <span className='rules__rule__hint'>{rule.hint}</span>
      {rule.priority !== null && (
        <p className='rules__rule__priority'>
          <span className='rules__rule__priority__label'>
            <FormattedMessage id='admin.rule.priority' defaultMessage='Priority:' />
          </span>{' '}
          {rule.priority}
        </p>
      )}
      <div className='rules__rule__actions'>
        <button onClick={handleEditRule(rule)}>
          <FormattedMessage id='admin.rules.edit' defaultMessage='Edit' />
        </button>
        <button onClick={handleDeleteRule(rule.id)}>
          <FormattedMessage id='admin.rules.delete' defaultMessage='Delete' />
        </button>
      </div>
    </div>
  );
};

const RulesPage: React.FC = () => {
  const intl = useIntl();

  const { openModal } = useModalsActions();
  const { data = [], isLoading } = useRules();

  const handleCreateRule = () => {
    openModal('EDIT_RULE');
  };

  const emptyMessage = (
    <FormattedMessage
      id='empty_column.admin.rules'
      defaultMessage='There are no instance rules yet.'
    />
  );

  return (
    <Column
      label={intl.formatMessage(messages.heading)}
      bodyClassName='rules'
      action={
        <button className='rules__create' onClick={handleCreateRule}>
          <Icon src={iconPlus} aria-hidden />
          <FormattedMessage id='admin.rules.action' defaultMessage='Create rule' />
        </button>
      }
    >
      <ScrollableList
        scrollKey='rules'
        emptyMessageText={emptyMessage}
        itemClassName='rules__rule__container'
        isLoading={isLoading}
        showLoading={isLoading}
      >
        {data.map((rule) => (
          <Rule key={rule.id} rule={rule} />
        ))}
      </ScrollableList>
    </Column>
  );
};

export { RulesPage as default };
